import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './FormPopup.css';

const FormPopup = ({ isOpen, onClose, formType }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    message: '',
    website: '',
    employees: '',
    goals: '',
    timeline: '',
    budget: '',
    services: [],
    priority: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [step, setStep] = useState(1);
  const [formValid, setFormValid] = useState(false);

  // Prevent body scroll when popup is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        industry: '',
        message: '',
        website: '',
        employees: '',
        goals: '',
        timeline: '',
        budget: '',
        services: [],
        priority: '',
      });
      setStep(1);
      setSubmitSuccess(false);
      setSubmitError('');
    }
  }, [isOpen]);

  // Validate form based on current step
  useEffect(() => {
    if (formType === 'summer') {
      // Summer offer form validation
      if (step === 1) {
        setFormValid(
          formData.name.trim() !== '' && 
          formData.email.trim() !== '' && 
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
          formData.company.trim() !== ''
        );
      } else if (step === 2) {
        setFormValid(
          formData.website.trim() !== '' && 
          formData.industry.trim() !== '' && 
          formData.employees.trim() !== ''
        );
      } else if (step === 3) {
        setFormValid(formData.message.trim() !== '');
      }
    } else {
      // General CTA form validation
      if (step === 1) {
        setFormValid(
          formData.name.trim() !== '' && 
          formData.email.trim() !== '' && 
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
        );
      } else if (step === 2) {
        setFormValid(
          formData.company.trim() !== '' && 
          formData.phone.trim() !== '' && 
          formData.website.trim() !== '' && 
          formData.services.length > 0 && 
          formData.priority.trim() !== ''
        );
      }
    }
  }, [formData, step, formType]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    
    if (name === 'services') {
      // Handle multi-select checkboxes for services
      if (value === 'all') {
        // If 'All' is selected, either select all services or none
        const allServices = ['website', 'seo', 'social', 'content', 'ads'];
        if (checked) {
          setFormData(prev => ({
            ...prev,
            services: allServices
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            services: []
          }));
        }
      } else {
        // Handle individual service selection
        setFormData(prev => {
          if (checked) {
            // Add the service if checked
            return {
              ...prev,
              services: [...prev.services, value]
            };
          } else {
            // Remove the service if unchecked
            return {
              ...prev,
              services: prev.services.filter(service => service !== value)
            };
          }
        });
      }
    } else {
      // Handle all other form fields normally
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const nextStep = () => {
    if (formType === 'summer' && step < 3) {
      setStep(step + 1);
    } else if (formType === 'general' && step < 2) {
      setStep(step + 1);
    }
  };
  
  // Check if a service is selected
  const isServiceSelected = (service) => {
    return formData.services.includes(service);
  };
  
  // Check if all services are selected
  const areAllServicesSelected = () => {
    const allServices = ['website', 'seo', 'social', 'content', 'ads'];
    return allServices.every(service => formData.services.includes(service));
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          access_key: '2b064546-285b-4fc5-b424-24af565b931d',
          subject: formType === 'summer' 
            ? 'New Summer Offer Application' 
            : 'New Client Inquiry',
          from_name: 'Enzlo Website Form',
          form_type: formType,
          ...formData
        })
      });

      const result = await response.json();
      if (result.success) {
        setSubmitSuccess(true);
      } else {
        setSubmitError(t('formErrorGeneric'));
      }
    } catch (error) {
      setSubmitError(t('formErrorGeneric'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div className="form-popup-overlay" onClick={onClose}>
      <div className="form-popup" onClick={e => e.stopPropagation()}>
        <button className="form-popup-close" onClick={onClose} aria-label={t('formClose')}>
          <span className="form-popup-close-icon"></span>
        </button>
        
        {submitSuccess ? (
          <div className="form-success">
            <h2>{t(formType === 'summer' ? 'formSummerSuccessTitle' : 'formGeneralSuccessTitle')}</h2>
            <p>{t(formType === 'summer' ? 'formSummerSuccessMessage' : 'formGeneralSuccessMessage')}</p>
            <button className="form-button" onClick={onClose}>{t('formSuccessClose')}</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <h2>{t(formType === 'summer' ? 'formSummerTitle' : 'formGeneralTitle')}</h2>
            
            {/* Progress indicator */}
            <div className="form-progress">
              {formType === 'summer' ? (
                <>
                  <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>1</div>
                  <div className="progress-line"></div>
                  <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>2</div>
                  <div className="progress-line"></div>
                  <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>3</div>
                </>
              ) : (
                <>
                  <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>1</div>
                  <div className="progress-line"></div>
                  <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>2</div>
                </>
              )}
            </div>
            
            {/* Summer Offer Form */}
            {formType === 'summer' && (
              <>
                {step === 1 && (
                  <div className="form-step">
                    <h3>{t('formSummerStep1Title')}</h3>
                    <div className="form-field">
                      <label htmlFor="name">{t('formName')} *</label>
                      <input 
                        type="text" 
                        id="name" 
                        name="name" 
                        value={formData.name} 
                        onChange={handleChange} 
                        required 
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="email">{t('formEmail')} *</label>
                      <input 
                        type="email" 
                        id="email" 
                        name="email" 
                        value={formData.email} 
                        onChange={handleChange} 
                        required 
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="company">{t('formCompany')} *</label>
                      <input 
                        type="text" 
                        id="company" 
                        name="company" 
                        value={formData.company} 
                        onChange={handleChange} 
                        required 
                      />
                    </div>
                  </div>
                )}
                
                {step === 2 && (
                  <div className="form-step">
                    <h3>{t('formSummerStep2Title')}</h3>
                    <div className="form-field">
                      <label htmlFor="website">{t('formWebsite')} *</label>
                      <input 
                        type="url" 
                        id="website" 
                        name="website" 
                        value={formData.website} 
                        onChange={handleChange} 
                        required 
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="industry">{t('formIndustry')} *</label>
                      <select 
                        id="industry" 
                        name="industry" 
                        value={formData.industry} 
                        onChange={handleChange} 
                        required
                      >
                        <option value="">{t('formSelectOption')}</option>
                        <option value="handyman">{t('formIndustryHandyman')}</option>
                        <option value="beauty">{t('formIndustryBeauty')}</option>
                        <option value="plumbing">{t('formIndustryPlumbing')}</option>
                        <option value="electrical">{t('formIndustryElectrical')}</option>
                        <option value="landscaping">{t('formIndustryLandscaping')}</option>
                        <option value="hair">{t('formIndustryHair')}</option>
                        <option value="other">{t('formIndustryOther')}</option>
                      </select>
                    </div>
                    <div className="form-field">
                      <label htmlFor="employees">{t('formEmployees')} *</label>
                      <select 
                        id="employees" 
                        name="employees" 
                        value={formData.employees} 
                        onChange={handleChange} 
                        required
                      >
                        <option value="">{t('formSelectOption')}</option>
                        <option value="1">1</option>
                        <option value="2-5">2-5</option>
                        <option value="6-10">6-10</option>
                        <option value="11-20">11-20</option>
                        <option value="21+">21+</option>
                      </select>
                    </div>
                  </div>
                )}
                
                {step === 3 && (
                  <div className="form-step">
                    <h3>{t('formSummerStep3Title')}</h3>
                    <div className="form-field">
                      <label htmlFor="message">{t('formSummerMessage')} *</label>
                      <textarea 
                        id="message" 
                        name="message" 
                        value={formData.message} 
                        onChange={handleChange} 
                        required
                        rows="4"
                      ></textarea>
                    </div>
                  </div>
                )}
              </>
            )}
            
            {/* General CTA Form */}
            {formType === 'general' && (
              <>
                {step === 1 && (
                  <div className="form-step">
                    <h3>{t('formGeneralStep1Title')}</h3>
                    <div className="form-field">
                      <label htmlFor="name">{t('formName')} *</label>
                      <input 
                        type="text" 
                        id="name" 
                        name="name" 
                        value={formData.name} 
                        onChange={handleChange} 
                        required 
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="email">{t('formEmail')} *</label>
                      <input 
                        type="email" 
                        id="email" 
                        name="email" 
                        value={formData.email} 
                        onChange={handleChange} 
                        required 
                      />
                    </div>
                  </div>
                )}
                
                {step === 2 && (
                  <div className="form-step">
                    <h3>{t('formGeneralStep2Title')}</h3>
                    <div className="form-field">
                      <label htmlFor="company">{t('formCompany')} *</label>
                      <input 
                        type="text" 
                        id="company" 
                        name="company" 
                        value={formData.company} 
                        onChange={handleChange} 
                        required 
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="phone">{t('formPhone')} *</label>
                      <input 
                        type="tel" 
                        id="phone" 
                        name="phone" 
                        value={formData.phone} 
                        onChange={handleChange} 
                        required 
                        placeholder="+49 123 456789"
                      />
                    </div>
                    <div className="form-field">
                      <label htmlFor="website">{t('formWebsite')} *</label>
                      <input 
                        type="url" 
                        id="website" 
                        name="website" 
                        value={formData.website} 
                        onChange={handleChange} 
                        required 
                        placeholder="https://"
                      />
                    </div>
                    <div className="form-field">
                      <label>{t('formServices')} *</label>
                      <div className="checkbox-group">
                        <div className="checkbox-item">
                          <input 
                            type="checkbox" 
                            id="service-all" 
                            name="services" 
                            value="all" 
                            checked={areAllServicesSelected()}
                            onChange={handleChange} 
                          />
                          <label htmlFor="service-all">{t('formServicesAll')}</label>
                        </div>
                        <div className="checkbox-item">
                          <input 
                            type="checkbox" 
                            id="service-website" 
                            name="services" 
                            value="website" 
                            checked={isServiceSelected('website')}
                            onChange={handleChange} 
                          />
                          <label htmlFor="service-website">{t('formServicesWebsite')}</label>
                        </div>
                        <div className="checkbox-item">
                          <input 
                            type="checkbox" 
                            id="service-seo" 
                            name="services" 
                            value="seo" 
                            checked={isServiceSelected('seo')}
                            onChange={handleChange} 
                          />
                          <label htmlFor="service-seo">{t('formServicesSEO')}</label>
                        </div>
                        <div className="checkbox-item">
                          <input 
                            type="checkbox" 
                            id="service-social" 
                            name="services" 
                            value="social" 
                            checked={isServiceSelected('social')}
                            onChange={handleChange} 
                          />
                          <label htmlFor="service-social">{t('formServicesSocial')}</label>
                        </div>
                        <div className="checkbox-item">
                          <input 
                            type="checkbox" 
                            id="service-content" 
                            name="services" 
                            value="content" 
                            checked={isServiceSelected('content')}
                            onChange={handleChange} 
                          />
                          <label htmlFor="service-content">{t('formServicesContent')}</label>
                        </div>
                        <div className="checkbox-item">
                          <input 
                            type="checkbox" 
                            id="service-ads" 
                            name="services" 
                            value="ads" 
                            checked={isServiceSelected('ads')}
                            onChange={handleChange} 
                          />
                          <label htmlFor="service-ads">{t('formServicesAds')}</label>
                        </div>
                      </div>
                    </div>
                    <div className="form-field">
                      <label htmlFor="priority">{t('formPriority')} *</label>
                      <select
                        id="priority"
                        name="priority"
                        value={formData.priority}
                        onChange={handleChange}
                        required
                      >
                        <option value="">{t('formSelectOption')}</option>
                        <option value="urgent">{t('formPriorityUrgent')}</option>
                        <option value="high">{t('formPriorityHigh')}</option>
                        <option value="medium">{t('formPriorityMedium')}</option>
                        <option value="low">{t('formPriorityLow')}</option>
                      </select>
                    </div>
                    <div className="form-field">
                      <label htmlFor="message">{t('formGeneralMessage')}</label>
                      <textarea 
                        id="message" 
                        name="message" 
                        value={formData.message} 
                        onChange={handleChange}
                        rows="3"
                        placeholder="Tell us about your project or requirements..."
                      ></textarea>
                    </div>
                  </div>
                )}
              </>
            )}
            
            {submitError && <div className="form-error">{submitError}</div>}
            
            <div className="form-actions">
              {step > 1 && (
                <button 
                  type="button" 
                  className="form-button secondary" 
                  onClick={prevStep}
                  disabled={isSubmitting}
                >
                  {t('formBack')}
                </button>
              )}
              
              {((formType === 'summer' && step < 3) || (formType === 'general' && step < 2)) ? (
                <button 
                  type="button" 
                  className="form-button" 
                  onClick={nextStep}
                  disabled={!formValid || isSubmitting}
                >
                  {t('formNext')}
                </button>
              ) : (
                <button 
                  type="submit" 
                  className="form-button" 
                  disabled={!formValid || isSubmitting}
                >
                  {isSubmitting ? t('formSubmitting') : t('formSubmit')}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FormPopup;
